import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, HelpCircle, AlertCircle, SearchX } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { StocksRawTableProps, StockRawRecord, StockRawResultData, StockRawCounts } from "./types";
import ResizableStocksTable from "./ResizableStocksTable";
import { MobileViewportWarning } from '@/components/ui/MobileViewportWarning';
import { Spinner } from '@/components/ui/Spinner';
import { getRowsPerPagePref, setRowsPerPagePref } from '@/utils/tablePreferences';

// Inline viewport utility since import is having issues
function isSmallViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 480; // Align with MobileViewportWarning threshold
}

export function StocksRawTable({
  filters = {},
  onAddToPortfolio,
  onFavorite,
  onAddToWatchlist,
  initialPage = 1,
  initialRowsPerPage = 25,
  queryEnabled = true,
  onCountsUpdate,
  stocks: initialStocks = [],
  isLoading: externalLoading = false,
  bulkSelection,
  onSetGetSelectedData,
}: StocksRawTableProps) {
  // Component state
  const [currentPage, setCurrentPage] = useState<number>(initialPage || 1);
  const [pageInputValue, setPageInputValue] = useState<string>("1");
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => getRowsPerPagePref('stocks-raw', initialRowsPerPage || 25));
  const [isSmallScreen, setIsSmallScreen] = useState(() => isSmallViewport());
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setPageInputValue("1");
  }, [filters]);
  
  // Update page input when currentPage changes
  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);
  
  // Responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(isSmallViewport());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Fetch data with React Query
  const fetchStocks = useCallback(async () => {
    try {
      // Calculate range for pagination
      const from = (currentPage - 1) * rowsPerPage;
      const to = from + rowsPerPage - 1;
      
      let query = supabase
        .from('stock_fundamentals')
        .select('*', { count: 'exact' })
        .range(from, to)
        .order('name', { ascending: true });

      // Apply filters if provided
      if (filters.searchText) {
        query = query.or(`name.ilike.%${filters.searchText}%,code.ilike.%${filters.searchText}%`);
      }

      if (filters.type) {
        query = query.eq('type', filters.type);
      }

      if (filters.exchange) {
        query = query.eq('exchange_name', filters.exchange);
      }

      const { data, count, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      return { data: data || [], count: count || 0 } as StockRawResultData;
    } catch (error) {
      console.error('Error fetching stocks:', error);
      throw error;
    }
  }, [currentPage, rowsPerPage, filters]);
  
  const { data: queryData, isLoading, error } = useQuery({
    queryKey: ['stocks', JSON.stringify(filters), currentPage, rowsPerPage],
    queryFn: () => fetchStocks(),
    enabled: queryEnabled,
    placeholderData: (prev) => prev, // This replaces keepPreviousData
    staleTime: 30000,
    refetchOnWindowFocus: false,
  }) as { data: StockRawResultData | undefined; isLoading: boolean; error: Error | null }; // Type assertion to fix downstream issues

  
  // Derive stocks and counts from query data
  const stocks = useMemo(() => {
    return (queryData as StockRawResultData | undefined)?.data || initialStocks;
  }, [queryData, initialStocks]);
  
  const totalCount = useMemo(() => {
    return (queryData as StockRawResultData | undefined)?.count || 0;
  }, [queryData]);
  const visibleCount = useMemo(() => stocks?.length || 0, [stocks]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / rowsPerPage)), [totalCount, rowsPerPage]);
  
  // Update counts for parent component
  useEffect(() => {
    if (onCountsUpdate) {
      onCountsUpdate({
        total: totalCount,
        filtered: totalCount, // Same as total when using server-side filtering
        visible: visibleCount,
      });
    }
  }, [totalCount, visibleCount, onCountsUpdate]);

  // Callback function to get selected stocks for bulk operations
  const getSelectedStocks = useCallback(() => {
    if (!bulkSelection || !bulkSelection.isSelected || !(stocks?.length || 0)) {
      return [];
    }
    return stocks.filter(stock => bulkSelection.isSelected(stock.id));
  }, [bulkSelection?.isSelected, stocks]);
  
  // Set up callback for parent component to get selected stocks
  useEffect(() => {
    if (onSetGetSelectedData && getSelectedStocks) {
      onSetGetSelectedData(getSelectedStocks);
    }
  }, [onSetGetSelectedData, getSelectedStocks]);
  
  // Page change handlers
  const handlePageChange = useCallback((page: number) => {
    const validPage = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(validPage);
    setPageInputValue(validPage.toString());
  }, [totalPages]);
  
  const handleRowsPerPageChange = useCallback((value: string) => {
    const newRowsPerPage = parseInt(value, 10);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    setPageInputValue("1");
    setRowsPerPagePref('stocks-raw', newRowsPerPage);
  }, []);
  
  // Handle page input changes
  const handlePageInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInputValue(e.target.value.replace(/[^0-9]/g, ''));
  }, []);
  
  const handlePageInputBlur = useCallback(() => {
    const page = parseInt(pageInputValue, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    } else {
      setPageInputValue(currentPage.toString());
    }
  }, [pageInputValue, totalPages, currentPage]);
  
  // Action handlers adapters
  const handleAddToPortfolio = useCallback((stockId: string, stock?: StockRawRecord) => {
    onAddToPortfolio(stockId, stock);
  }, [onAddToPortfolio]);
  
  const handleFavorite = useCallback((stockId: string) => {
    onFavorite(stockId);
  }, [onFavorite]);
  
  const handleAddToWatchlist = useCallback((stockId: string) => {
    if (onAddToWatchlist) {
      onAddToWatchlist(stockId);
    }
  }, [onAddToWatchlist]);
  
  // Determine loading state
  const isLoadingState = isLoading || externalLoading;
  
  return (
    <div>
      
      {/* Table content - show on desktop only, hide on mobile and when error */}
      {!error && !isSmallScreen && (
        <div className="space-y-4">
          <AnimatePresence mode="wait">
            {isLoadingState && (stocks?.length || 0) === 0 ? (
              // Loading skeleton - proper table structure matching column widths
            <motion.div
              key="skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full"
            >
              <div className="relative w-full overflow-auto">
                <table className="w-full caption-bottom text-sm">
                  <thead className="[&_tr]:border-b">
                    <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                      {bulkSelection && (
                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[40px]">
                          <div className="h-4 w-4 bg-muted rounded animate-pulse-fast"></div>
                        </th>
                      )}
                      <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[100px]">
                        <div className="h-4 w-16 bg-muted rounded animate-pulse-fast"></div>
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[250px]">
                        <div className="h-4 w-24 bg-muted rounded animate-pulse-fast"></div>
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[150px]">
                        <div className="h-4 w-20 bg-muted rounded animate-pulse-fast"></div>
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[120px]">
                        <div className="h-4 w-24 bg-muted rounded animate-pulse-fast"></div>
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[150px]">
                        <div className="h-4 w-16 bg-muted rounded animate-pulse-fast"></div>
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[60px]">
                        <div className="h-4 w-12 bg-muted rounded animate-pulse-fast"></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child]:border-0">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <motion.tr
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05, duration: 0.3 }}
                        className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                      >
                        {bulkSelection && (
                          <td className="p-4 align-middle w-[40px]">
                            <div className="h-4 w-4 bg-muted rounded animate-pulse-fast"></div>
                          </td>
                        )}
                        <td className="p-4 align-middle w-[100px]">
                          <div className="h-4 w-16 bg-muted rounded animate-pulse-fast"></div>
                        </td>
                        <td className="p-4 align-middle w-[250px]">
                          <div className="h-4 w-32 bg-muted rounded animate-pulse-fast"></div>
                        </td>
                        <td className="p-4 align-middle w-[150px]">
                          <div className="h-4 w-20 bg-muted rounded animate-pulse-fast"></div>
                        </td>
                        <td className="p-4 align-middle w-[120px]">
                          <div className="h-4 w-24 bg-muted rounded animate-pulse-fast"></div>
                        </td>
                        <td className="p-4 align-middle w-[150px]">
                          <div className="h-4 w-20 bg-muted rounded animate-pulse-fast"></div>
                        </td>
                        <td className="p-4 align-middle w-[60px]">
                          <div className="flex gap-1">
                            <div className="h-8 w-8 bg-muted rounded animate-pulse-fast"></div>
                            <div className="h-8 w-8 bg-muted rounded animate-pulse-fast"></div>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          ) : (
            // Render the table with data
            <motion.div
              key="table"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full">
              <ResizableStocksTable
                data={stocks}
                isLoading={isLoadingState}
                onAddToPortfolio={handleAddToPortfolio}
                onFavorite={handleFavorite}
                onAddToWatchlist={handleAddToWatchlist}
                bulkSelection={bulkSelection}
              />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Loading and empty states */}
        {isLoadingState && (
          <div className="p-4 text-center text-muted-foreground flex items-center justify-center gap-2">
            <Spinner size="sm" variant="muted" ariaLabel="Loading stocks" />
            <span>Loading…</span>
          </div>
        )}
        {!isLoadingState && (stocks?.length || 0) === 0 && (
          <div className="border rounded-md p-8 text-center w-[98%] my-4 mx-auto">
            <SearchX className="h-16 w-16 text-muted-foreground mb-4 mx-auto" />
            <h3 className="text-lg font-medium mb-2">No stocks found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        )}
        </div>
      )}
      
      {/* Error state - show on all screen sizes */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load stocks. Please try again later.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Pagination controls - styled after Supabase UI - hide on mobile */}
      {!error && !isSmallScreen && ((stocks?.length || 0) > 0 || isLoadingState) && (
        <div className="border-t p-2 flex items-center justify-between bg-muted/20">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1 || isLoadingState}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center">
              <Input
                className="h-8 w-12 px-1 text-center"
                value={pageInputValue}
                onChange={handlePageInputChange}
                onBlur={handlePageInputBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePageInputBlur();
                  }
                }}
                disabled={isLoadingState}
              />
              <span className="text-sm mx-2">of {totalPages}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || isLoadingState}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex items-center space-x-2">
            <p className="text-sm text-muted-foreground">
              Rows per page
            </p>
            <select
              value={rowsPerPage}
              onChange={(e) => handleRowsPerPageChange(e.target.value)}
              className="border border-input rounded-md h-8 px-2 text-sm"
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="500">500</option>
              <option value="1000">1000</option>
            </select>
            
            <p className="text-sm text-muted-foreground ml-4">
              Showing {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount}
            </p>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    This table shows stocks from our database. You can filter by type and exchange using the dropdown menus above.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        )}
    </div>
  );
}

export default StocksRawTable;
